<section class="canvas-card">
    <header class="card-header">
        <div class="card-copy">
            <h2 appLocalizationPopupDirective localizationCode="FormElements">{{ 'FormElements' | translate: {Default: "Form elements"} }}</h2>
            <p>{{ 'FormElementsDescription' | translate: {Default: "Drag elements to reorder them. New order saves automatically."} }}</p>
        </div>

        <div class="order-status" [ngClass]="orderStatus" *ngIf="orderStatus !== 'idle'" role="status">
            <ng-container [ngSwitch]="orderStatus">
                <ng-container *ngSwitchCase="'saving'">
                    <mat-spinner diameter="14" strokeWidth="2"></mat-spinner>
                    {{ 'SavingOrder' | translate: {Default: "Saving order…"} }}
                </ng-container>
                <ng-container *ngSwitchCase="'saved'">
                    <mat-icon class="material-icons-outlined" aria-hidden="true">cloud_done</mat-icon>
                    {{ 'OrderSaved' | translate: {Default: "Order saved"} }}
                </ng-container>
                <ng-container *ngSwitchCase="'error'">
                    <mat-icon class="material-icons-outlined" aria-hidden="true">error_outline</mat-icon>
                    {{ 'OrderNotSaved' | translate: {Default: "Order not saved"} }}
                </ng-container>
            </ng-container>
        </div>
    </header>

    <ng-container *ngIf="!isFinishedLoading(); else loadedFormElements">
        <ngx-skeleton-loader count="5" [theme]="{ 'height.px': 72, 'border-radius': '12px' }"></ngx-skeleton-loader>
    </ng-container>

    <ng-template #loadedFormElements>
        <div class="empty-state" *ngIf="displayElements.length === 0">
            <div class="empty-icon">
                <mat-icon class="material-icons-outlined" aria-hidden="true">dynamic_form</mat-icon>
            </div>
            <h3>{{ 'NoFormElements' | translate: {Default: "No elements yet"} }}</h3>
            <p>{{ 'NoFormElementsDescription' | translate: {Default: "Add fields and sections to build what your users will fill in."} }}</p>
            <button mat-flat-button class="rounded-button" setColor="primaryColour" color="primary" appPreventDoubleClick (throttledClick)="addElement()">
                <mat-icon class="material-icons-outlined" aria-hidden="true">add</mat-icon>
                {{ 'AddFirstElement' | translate: {Default: "Add your first element"} }}
            </button>
        </div>

        <div class="element-list" cdkDropList (cdkDropListDropped)="drop($event, displayElements)">
            <div class="element-drag" *ngFor="let formElement of displayElements" cdkDrag cdkDragLockAxis="y">
                <div class="drop-placeholder" *cdkDragPlaceholder></div>

                <!-- Field -->
                <div class="element-row" *ngIf="formElement.formElementType.id !== fieldTypes.CONTAINER; else containerElement">
                    <button type="button" class="drag-handle" cdkDragHandle [attr.aria-label]="'DragToReorder' | translate: {Default: 'Drag to reorder'}">
                        <mat-icon class="material-icons-outlined" aria-hidden="true">drag_indicator</mat-icon>
                    </button>
                    <ng-container *ngTemplateOutlet="elementContent; context: { $implicit: formElement }"></ng-container>
                </div>

                <!-- Section -->
                <ng-template #containerElement>
                    <div class="section-card">
                        <div class="section-header">
                            <button type="button" class="drag-handle" cdkDragHandle [attr.aria-label]="'DragToReorder' | translate: {Default: 'Drag to reorder'}">
                                <mat-icon class="material-icons-outlined" aria-hidden="true">drag_indicator</mat-icon>
                            </button>

                            <div class="section-icon">
                                <mat-icon class="material-icons-outlined" aria-hidden="true">view_agenda</mat-icon>
                            </div>

                            <div class="section-title">
                                <h3>{{ formElement.text }}</h3>
                                <div class="element-meta">
                                    <span class="type-label">
                                        {{ 'Section' | translate: {Default: "Section"} }}
                                        <span class="count">{{ formElement.children?.length || 0 }}</span>
                                    </span>
                                    <ng-container *ngTemplateOutlet="badges; context: { $implicit: formElement }"></ng-container>
                                </div>
                            </div>

                            <ng-container *ngTemplateOutlet="actions; context: { $implicit: formElement }"></ng-container>
                        </div>

                        <div class="section-body">
                            <div class="element-list child-list" cdkDropList (cdkDropListDropped)="drop($event, formElement.children)">
                                <div class="element-drag" *ngFor="let childElement of formElement.children" cdkDrag cdkDragLockAxis="y">
                                    <div class="drop-placeholder" *cdkDragPlaceholder></div>

                                    <div class="element-row">
                                        <button type="button" class="drag-handle" cdkDragHandle [attr.aria-label]="'DragToReorder' | translate: {Default: 'Drag to reorder'}">
                                            <mat-icon class="material-icons-outlined" aria-hidden="true">drag_indicator</mat-icon>
                                        </button>
                                        <ng-container *ngTemplateOutlet="elementContent; context: { $implicit: childElement }"></ng-container>
                                    </div>
                                </div>
                            </div>

                            <button type="button" class="add-zone" appPreventDoubleClick (throttledClick)="addChildElement(formElement)">
                                <mat-icon class="material-icons-outlined" aria-hidden="true">add</mat-icon>
                                {{ 'AddElementToSection' | translate: {Default: "Add element to this section"} }}
                            </button>
                        </div>
                    </div>
                </ng-template>
            </div>
        </div>
    </ng-template>
</section>

<!-- Everything in a field row after its drag handle -->
<ng-template #elementContent let-element>
    <div class="element-body">
        <div class="element-meta">
            <span class="type-label">
                <mat-icon class="material-icons-outlined" aria-hidden="true">{{ typeIcon(element) }}</mat-icon>
                {{ element.formElementType?.name }}
            </span>
            <ng-container *ngTemplateOutlet="badges; context: { $implicit: element }"></ng-container>
        </div>

        <div class="element-preview">
            <app-form-field [field]="element"></app-form-field>
        </div>
    </div>

    <ng-container *ngTemplateOutlet="actions; context: { $implicit: element }"></ng-container>
</ng-template>

<ng-template #badges let-element>
    <ng-container *ngIf="elementFlags[element.id] as flags">
        <span class="badge required" *ngIf="flags.required">{{ 'Required' | translate: {Default: "Required"} }}</span>
        <span class="badge neutral" *ngIf="flags.readOnly">
            <mat-icon class="material-icons-outlined" aria-hidden="true">lock</mat-icon>
            {{ 'ReadOnly' | translate: {Default: "Read only"} }}
        </span>
        <span class="badge muted" *ngIf="flags.hidden">
            <mat-icon class="material-icons-outlined" aria-hidden="true">visibility_off</mat-icon>
            {{ 'Hidden' | translate: {Default: "Hidden"} }}
        </span>
        <span class="badge linked" *ngIf="flags.linked" [matTooltip]="element.tableField?.name" matTooltipPosition="above">
            <mat-icon class="material-icons-outlined" aria-hidden="true">link</mat-icon>
            {{ 'LinkedToTableField' | translate: {Default: "Table field"} }}
        </span>
        <span class="badge neutral" *ngIf="flags.helpText" [matTooltip]="flags.helpText" matTooltipPosition="above">
            <mat-icon class="material-icons-outlined" aria-hidden="true">help_outline</mat-icon>
            {{ 'HelpText' | translate: {Default: "Help text"} }}
        </span>
    </ng-container>
</ng-template>

<ng-template #actions let-element>
    <div class="element-actions">
        <button
            type="button"
            mat-icon-button
            class="icon-button"
            appPreventDoubleClick (throttledClick)="editElement(element)"
            [matTooltip]="'Edit' | translate: {Default: 'Edit'}"
            matTooltipPosition="above" [attr.aria-label]="'Edit' | translate: {Default: 'Edit'}">
            <mat-icon class="material-icons-outlined" aria-hidden="true">edit</mat-icon>
        </button>
        <button
            *ngIf="currentEmployeeDetails?.userType !== 'Employee'"
            type="button"
            mat-icon-button
            class="icon-button danger"
            appPreventDoubleClick (throttledClick)="openConfirmDeleteDialog(element)"
            [matTooltip]="'Delete' | translate: {Default: 'Delete'}"
            matTooltipPosition="above" [attr.aria-label]="'Delete' | translate: {Default: 'Delete'}">
            <mat-icon class="material-icons-outlined" aria-hidden="true">delete</mat-icon>
        </button>
    </div>
</ng-template>
